import type { GroupMessageReceiptInfo, ReceiptInfo } from '@openim/wasm-client-sdk';

import { getActions, getGlobal } from '../../../global';

import type { OpenImRawMessage } from '../appDto/types';

import { Bundles, loadBundle } from '../../../util/moduleLoader';
import { mapOpenImMessageToApiMessage } from '../appDto/message';
import { parseTgimMessageEditNotification } from '../methods/messageEdit';
import { subscribeOpenImEvents } from '../worker/connector';
import { onConversationRefreshed, requestConversationRefresh, setConversationSyncSelfUserId } from './conversationSync';
import { markOpenImServerSyncFinished, resetOpenImServerSyncState } from './serverSync';

// Event names verified against the OpenIM SDK's actual Go source (`../../openim-sdk-core`):
// `OnRecvNewMessage` (OnAdvancedMsgListener), `OnConversationChanged` / `OnNewConversation`
// (OnConversationListener), `OnNewRecvMessageRevoked` / `OnRecvMessageRevoked` — see
// `open_im_sdk_callback/callback_client.go`. The WASM bridge (`wasm/event_listener/listener.go`)
// sets each event's `event` field via `utils.GetSelfFuncName()`.
let currentUnsubscribe: (() => void) | undefined;
let currentUnsubscribeConversationRefresh: (() => void) | undefined;

// A bulk member invite/kick or role/mute change fires one `OnGroupMemberAdded`/
// `OnGroupMemberInfoChanged` event PER affected row (OpenIM's syncer diffs row-by-row), each
// otherwise triggering its own full `force: true` member-list reload -- bypassing
// `loadFullChat`'s own 500ms debounce, which only applies to its non-`force` callers. Debounce
// per group here instead so N events for the same group collapse into one refresh.
const GROUP_REFRESH_DEBOUNCE_MS = 500;
const pendingGroupRefreshTimers = new Map<string, number>();

function scheduleGroupMemberRefresh(groupId: string | undefined): void {
  const refresh = () => {
    void loadBundle(Bundles.Main).then(() => {
      if (groupId) {
        getActions().loadFullChat({ chatId: `sg_${groupId}`, force: true });
      }
      requestConversationRefresh(true);
    });
  };

  if (!groupId) {
    refresh();
    return;
  }

  const existing = pendingGroupRefreshTimers.get(groupId);
  if (existing !== undefined) {
    self.clearTimeout(existing);
  }
  pendingGroupRefreshTimers.set(groupId, self.setTimeout(() => {
    pendingGroupRefreshTimers.delete(groupId);
    refresh();
  }, GROUP_REFRESH_DEBOUNCE_MS));
}

// The WASM SDK's `tryParse` option (see `methods/auth.ts`'s login call) sometimes pre-parses an
// event's JSON string payload into a plain object before emitting it, and sometimes doesn't --
// calling `JSON.parse` unconditionally throws `SyntaxError: "[object Object]" is not valid JSON`
// once the object has already been coerced to a string by the `as string` cast. Every listener
// below must accept either shape.
function parseEventData<T>(data: unknown): T | undefined {
  if (!data) return undefined;
  if (typeof data === 'object') return data as T;
  if (typeof data !== 'string') return undefined;
  try {
    return JSON.parse(data) as T;
  } catch {
    return undefined;
  }
}

function parseRevokedClientMsgId(data: unknown): string | undefined {
  if (!data) return undefined;
  let obj: Record<string, unknown> | undefined;
  if (typeof data === 'string') {
    try {
      obj = JSON.parse(data) as Record<string, unknown>;
    } catch {
      // Legacy payload may be a bare clientMsgID string.
      const trimmed = data.trim();
      return trimmed || undefined;
    }
  } else if (typeof data === 'object' && !Array.isArray(data)) {
    obj = data as Record<string, unknown>;
  }
  const id = obj?.clientMsgID ?? obj?.clientMsgId;
  return typeof id === 'string' && id.trim() ? id.trim() : undefined;
}

/** Resolve TG mid + chatId from OpenIM clientMsgID (Android ActiveChatBridge.applyMessageRevoked). */
function findMessageByClientMsgId(clientMsgId: string): { chatId: string; id: number } | undefined {
  const global = getGlobal();
  const byChatId = global.messages?.byChatId || {};
  for (const [chatId, threadStore] of Object.entries(byChatId)) {
    const byId = threadStore?.byId || {};
    for (const message of Object.values(byId)) {
      if (!message) continue;
      if ((message as any)._tgimClientMsgId === clientMsgId) {
        return { chatId, id: message.id };
      }
    }
  }
  return undefined;
}

export function startOpenImRealtimeBridge(selfUserId: string): () => void {
  currentUnsubscribe?.();
  currentUnsubscribeConversationRefresh?.();

  setConversationSyncSelfUserId(selfUserId);

  currentUnsubscribeConversationRefresh = onConversationRefreshed((chats) => {
    void loadBundle(Bundles.Main).then(() => {
      getActions().mergeRefreshedOpenImConversations({ chats });
    });
  });

  // Eager refresh is fine as a best-effort preload, but Android treats pre-sync lists as often
  // empty — the authoritative refresh is the OnSyncServerFinish handler below.
  requestConversationRefresh(true);

  currentUnsubscribe = subscribeOpenImEvents((evt) => {
    if (evt.event === 'OnSyncServerStart') {
      resetOpenImServerSyncState();
      return;
    }

    if (evt.event === 'OnSyncServerFinish' || evt.event === 'OnSyncServerFailed') {
      // Failed still unblocks UI/send waiters (Android onSyncServerFailed); send may still error,
      // but the chat list must not spin forever.
      markOpenImServerSyncFinished();
      requestConversationRefresh(true);
      return;
    }

    if (evt.event === 'OnRecvNewMessage') {
      const raw = parseEventData<OpenImRawMessage>(evt.data);
      if (!raw) return;

      // Android `tryConsumeEditNotificationJson` — apply text edit, never show as a bubble.
      const edit = parseTgimMessageEditNotification(raw);
      if (edit) {
        const found = findMessageByClientMsgId(edit.clientMsgId);
        if (found) {
          const existingMsg = getGlobal().messages.byChatId[found.chatId]?.byId?.[found.id];
          getActions().apiUpdate({
            '@type': 'updateMessage',
            id: found.id,
            chatId: found.chatId,
            message: {
              ...(existingMsg || { id: found.id, chatId: found.chatId }),
              content: {
                ...(existingMsg?.content || {}),
                text: { text: edit.text },
              },
              isEdited: true,
              editDate: Math.floor(Date.now() / 1000),
            },
          });
        }
        return;
      }

      const clientMsgId = (raw.clientMsgID || '').trim();
      // Own sends already have an optimistic bubble keyed by clientMsgID. Pushing a second
      // `newMessage` with a different mid (then setting `previousLocalId`) produces duplicate
      // Teact keys for one frame and crashes the message list.
      if (clientMsgId) {
        const existing = findMessageByClientMsgId(clientMsgId);
        if (existing) {
          const mapped = mapOpenImMessageToApiMessage(raw, selfUserId);
          if (!mapped) return;
          getActions().apiUpdate({
            '@type': 'updateMessageSendSucceeded',
            chatId: existing.chatId,
            localId: existing.id,
            message: {
              ...mapped,
              id: existing.id,
              chatId: existing.chatId,
              sendingState: undefined,
            },
          });
          return;
        }
      }
      const message = mapOpenImMessageToApiMessage(raw, selfUserId);
      if (!message) return;
      getActions().apiUpdate({
        '@type': 'newMessage',
        chatId: message.chatId,
        id: message.id,
        message,
      });
      return;
    }

    // Peer revoke → same `deleteMessages` apiUpdate path Telegram WebA uses for MTP deletes.
    if (evt.event === 'OnNewRecvMessageRevoked' || evt.event === 'OnRecvMessageRevoked') {
      const clientMsgId = parseRevokedClientMsgId(evt.data);
      if (!clientMsgId) return;
      const found = findMessageByClientMsgId(clientMsgId);
      if (!found) return;
      getActions().apiUpdate({
        '@type': 'deleteMessages',
        ids: [found.id],
        chatId: found.chatId,
      });
      return;
    }

    // Peer read our outgoing message(s) → advance the checkmark from delivered to read
    // (Android TgImOpenImActiveChatBridge / iOS CocoaPodsOIMClientEngine both subscribe here).
    if (evt.event === 'OnRecvC2CReadReceipt') {
      const receipts = parseEventData<ReceiptInfo[]>(evt.data);
      if (!receipts) return;
      receipts.forEach((receipt) => {
        // A pure watermark receipt (no msgIDList, only userID + readTime) marks everything the
        // peer had sent up to that point as read -- can't be resolved via clientMsgID lookup, so
        // it's intentionally left unhandled here (Android/iOS don't fully cover it either; see
        // investigation notes) rather than guessed at.
        const readDate = Math.floor(receipt.readTime / 1000);
        (receipt.msgIDList || []).forEach((clientMsgId) => {
          const found = findMessageByClientMsgId(clientMsgId);
          if (!found) return;
          getActions().apiUpdate({
            '@type': 'updateMessage',
            id: found.id,
            chatId: found.chatId,
            message: { readDate },
          });
        });
      });
      return;
    }

    if (evt.event === 'OnRecvGroupReadReceipt') {
      const receipt = parseEventData<GroupMessageReceiptInfo>(evt.data);
      if (!receipt) return;
      (receipt.groupMessageReadInfo || []).forEach((info) => {
        const found = findMessageByClientMsgId(info.clientMsgID);
        if (!found) return;
        getActions().apiUpdate({
          '@type': 'updateMessage',
          id: found.id,
          chatId: found.chatId,
          message: { readDate: Math.floor(Date.now() / 1000) },
        });
      });
      return;
    }

    if (evt.event === 'OnConversationChanged' || evt.event === 'OnNewConversation') {
      requestConversationRefresh(true);
    }

    if (
      evt.event === 'OnGroupInfoChanged'
      || evt.event === 'OnGroupMemberInfoChanged'
      || evt.event === 'OnGroupMemberAdded'
      || evt.event === 'OnGroupMemberDeleted'
    ) {
      // Android bumps then refreshChatProfileFromApi — re-pull mute/admin rights for open groups.
      const groupId = parseGroupIdFromEventData(evt.data);
      scheduleGroupMemberRefresh(groupId);
      return;
    }

    if (evt.event === 'OnGroupDismissed') {
      requestConversationRefresh(true);
      return;
    }

    if (
      evt.event === 'OnGroupApplicationAdded'
      || evt.event === 'OnGroupApplicationDeleted'
      || evt.event === 'OnGroupApplicationAccepted'
      || evt.event === 'OnGroupApplicationRejected'
    ) {
      void loadBundle(Bundles.Main).then(() => {
        getActions().loadGroupApplications();
        if (evt.event === 'OnGroupApplicationAccepted') {
          requestConversationRefresh(true);
        }
      });
    }
  });

  return () => {
    currentUnsubscribe?.();
    currentUnsubscribe = undefined;
    currentUnsubscribeConversationRefresh?.();
    currentUnsubscribeConversationRefresh = undefined;
  };
}

export function stopOpenImRealtimeBridge(): void {
  currentUnsubscribe?.();
  currentUnsubscribe = undefined;
  currentUnsubscribeConversationRefresh?.();
  currentUnsubscribeConversationRefresh = undefined;
  pendingGroupRefreshTimers.forEach((timer) => self.clearTimeout(timer));
  pendingGroupRefreshTimers.clear();
}

function parseGroupIdFromEventData(data: unknown): string | undefined {
  if (!data) return undefined;
  let obj: unknown = data;
  if (typeof data === 'string') {
    try {
      obj = JSON.parse(data);
    } catch {
      return undefined;
    }
  }
  // Callbacks may send a single group, an array, or a member row with groupID.
  const rows = Array.isArray(obj) ? obj : [obj];
  for (const row of rows) {
    if (!row || typeof row !== 'object') continue;
    const gid = (row as { groupID?: string; groupId?: string }).groupID
      || (row as { groupID?: string; groupId?: string }).groupId;
    if (typeof gid === 'string' && gid.trim()) return gid.trim();
  }
  return undefined;
}
